import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getErrorMessage } from '../helper/error';
import { FormValues, ICollectionAdapter, IDraftRow } from '../interface/setup';

interface ICreateOp {
  key: string;
  form: FormValues;
}

interface IDeleteOp {
  id: string;
  // kept so a failed delete can still be named after the user moved off its page
  form: FormValues;
}

export interface ISyncFailure {
  form: FormValues;
  // backend's reason when it sent one
  message?: string;
}

interface ISyncResult {
  failures: ISyncFailure[];
}

export const PAGE_SIZES = [10, 25, 50];
const SEARCH_DEBOUNCE_MS = 400;

// Holds a list's add/edit/delete as local drafts; nothing reaches the backend
// until sync() replays them (creates, then edits, then deletes). The list itself
// is paged and searched on the backend, one page in memory at a time; drafts
// outlive page changes (new rows show on page 1, edits/deletes on their own page).
export const useDraftCollection = <TRow>(adapter: ICollectionAdapter<TRow>) => {
  const serverPaged = adapter.serverPaged !== false;

  const [fetched, setFetched] = useState<TRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [creates, setCreates] = useState<ICreateOp[]>([]);
  const [edits, setEdits] = useState<Record<string, FormValues>>({});
  const [deletes, setDeletes] = useState<IDeleteOp[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(PAGE_SIZES[0]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const appliedSearch = useRef('');

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (next === appliedSearch.current) return;
      appliedSearch.current = next;
      setSearch(next);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // endpoints that return everything are searched and paged in the browser: no refetch per keystroke/page
  const fetchPage = serverPaged ? page : 1;
  const fetchSize = serverPaged ? pageSize : 0;
  const fetchSearch = serverPaged ? search : '';

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const result = await adapter.list(
        serverPaged
          ? { page: fetchPage, limit: fetchSize, textSearch: fetchSearch }
          : { page: 1, limit: Number.MAX_SAFE_INTEGER }
      );
      // deleting the last row of a page leaves it empty: step back to the last page that has rows
      if (serverPaged && result.rows.length === 0 && result.totalItems > 0 && fetchPage > 1) {
        setPage(Math.max(1, Math.ceil(result.totalItems / fetchSize)));
        return;
      }
      setFetched(result.rows);
      setTotalItems(result.totalItems);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
    // adapter is a module-level constant per tab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage, fetchSize, fetchSearch, serverPaged]);

  useEffect(() => {
    load();
  }, [load]);

  // Browser-side search/paging for endpoints that return everything.
  const clientQuery = search.toLowerCase();
  const clientMatches = useMemo(() => {
    if (serverPaged) return fetched;
    if (!clientQuery) return fetched;
    return fetched.filter((row) =>
      Object.values(adapter.rowToForm(row))
        .filter((value) => typeof value === 'string' || typeof value === 'number')
        .join(' ')
        .toLowerCase()
        .includes(clientQuery)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetched, clientQuery, serverPaged]);

  const clientTotalPages = Math.max(1, Math.ceil(clientMatches.length / pageSize));
  useEffect(() => {
    if (!serverPaged && page > clientTotalPages) setPage(clientTotalPages);
  }, [serverPaged, page, clientTotalPages]);

  const pageRowsSource = serverPaged ? fetched : clientMatches.slice((page - 1) * pageSize, page * pageSize);
  const matchCount = serverPaged ? totalItems : clientMatches.length;

  const rows = useMemo<IDraftRow<TRow>[]>(() => {
    const existing = pageRowsSource.map((row): IDraftRow<TRow> => {
      const id = adapter.getId(row);
      const status = deletes.some((op) => op.id === id) ? 'deleted' : edits[id] ? 'edited' : 'saved';
      return { key: id, id, row, status, form: edits[id] ?? adapter.rowToForm(row) };
    });
    // drafts not saved yet have no server page: list them first, on page 1, when not searching
    const added =
      page === 1 && !search
        ? creates.map((op): IDraftRow<TRow> => ({ key: op.key, status: 'new', form: op.form }))
        : [];
    return [...added, ...existing];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRowsSource, creates, edits, deletes, page, search]);

  const pendingCount = creates.length + Object.keys(edits).length + deletes.length;

  useEffect(() => {
    if (pendingCount === 0) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pendingCount]);

  const add = (form: FormValues) => {
    setCreates((prev) => [...prev, { key: `new-${Date.now()}-${prev.length}`, form }]);
    setSearchInput('');
    appliedSearch.current = '';
    setSearch('');
    setPage(1);
  };

  const edit = (target: IDraftRow<TRow>, form: FormValues) => {
    if (target.status === 'new') {
      setCreates((prev) => prev.map((op) => (op.key === target.key ? { ...op, form } : op)));
    } else if (target.id) {
      setEdits((prev) => ({ ...prev, [target.id as string]: form }));
    }
  };

  const remove = (target: IDraftRow<TRow>) => {
    if (target.status === 'new') {
      setCreates((prev) => prev.filter((op) => op.key !== target.key));
    } else if (target.id) {
      setDeletes((prev) => [...prev, { id: target.id as string, form: target.form }]);
    }
  };

  // Drops this row's pending edit/delete, back to what is saved.
  const undo = (target: IDraftRow<TRow>) => {
    const id = target.id;
    if (!id) return;
    setDeletes((prev) => prev.filter((op) => op.id !== id));
    setEdits((prev) => Object.fromEntries(Object.entries(prev).filter(([editedId]) => editedId !== id)));
  };

  const sync = async (): Promise<ISyncResult> => {
    const failures: ISyncFailure[] = [];
    const remainingCreates: ICreateOp[] = [];
    const remainingEdits: Record<string, FormValues> = {};
    const remainingDeletes: IDeleteOp[] = [];

    for (const op of creates) {
      try {
        await adapter.create(op.form);
      } catch (error) {
        failures.push({ form: op.form, message: getErrorMessage(error) });
        remainingCreates.push(op);
      }
    }
    for (const [id, form] of Object.entries(edits)) {
      if (deletes.some((op) => op.id === id)) continue;
      try {
        await adapter.update(id, form);
      } catch (error) {
        failures.push({ form, message: getErrorMessage(error) });
        remainingEdits[id] = form;
      }
    }
    for (const op of deletes) {
      try {
        await adapter.remove(op.id);
      } catch (error) {
        failures.push({ form: op.form, message: getErrorMessage(error) });
        remainingDeletes.push(op);
      }
    }

    setCreates(remainingCreates);
    setEdits(remainingEdits);
    setDeletes(remainingDeletes);
    await load();

    return { failures };
  };

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setPage(1);
  };

  return {
    rows,
    loading,
    loadFailed,
    pendingCount,
    add,
    edit,
    remove,
    undo,
    sync,
    reload: load,
    // paging / search
    page,
    pageSize,
    totalItems: matchCount,
    totalPages: Math.max(1, Math.ceil(matchCount / pageSize)),
    searchInput,
    setSearchInput,
    setPage,
    setPageSize,
  };
};
